import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl } from '@angular/forms';
import { Observable } from 'rxjs';

import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { OrganizationService } from '../../../../services/organization/organization.service';
import { StripeService } from '../../../../services/billing/stripe.service';

import { CreditCard } from '../../../../models/entities/credit-card';
import { CreditCardCollectorComponent } from '../credit-card-collector/credit-card-collector.component';
import { PaymentMethod } from '../../../../services/billing/payment-method.model';
import { PaymentMethodsService } from '../../../../services/financial-accounts/payment-methods.service';
import { BankAccountsService } from '../../../../services/financial-accounts/bank-accounts.service';
import { PaymentBankAccount } from '../../../../models/entities/payment-bank-account';
import { Organization } from '../../../../models/entities/organization';
import { BankAccountManagerComponent } from '../bank-account-manager/bank-account-manager.component';
import { PlaidService } from '../../../../services/plaid.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

// TODO ------ make this component way less complicated and nested iffy; and add comments to make it intelligible

const noop = () => {
};

declare var Plaid: any;

@UntilDestroy()
@Component({
    selector: 'lf-payment-method-selector',
    styleUrls: ['./payment-method-selector.scss'],
    templateUrl: './payment-method-selector.component.html',
})
export class PaymentMethodSelectorComponent implements ControlValueAccessor, OnInit
{
    @Input() organizationId: number;
    @Input() orgCharges = false;
    @Input() disableBanks = false;
    @Input() showFeeNotification = false;
    @Input() ccSavable = false;
    @Input() disabled = false;
    @Input() isPaymentMethodNotSelected = false;
    @Input() organization: Organization = null;
    @Input() doStackColumns = false;

    @Output() modelChange: EventEmitter<PaymentMethod> = new EventEmitter<PaymentMethod>();

    @ViewChild('bankAccountManagerComponent')
    bankAccountManagerComponent: BankAccountManagerComponent;

    @ViewChild('creditCardCollector', {read: CreditCardCollectorComponent})
    creditCardCollector: CreditCardCollectorComponent;

    paymentBankAccounts: PaymentBankAccount[] = [];
    creditCards: CreditCard[] = [];
    paymentMethods: PaymentMethod[] = [];
    defaultPaymentMethods: PaymentMethod[] = [
        {type: 'PaymentBankAccount', value: 'new-bank-account'},
        {type: 'CreditCard', value: 'new-credit-card'},
    ];
    isLoadingPaymentMethods = true;
    isUserFlow = false;
    microdepositVerificationInputs: any = {
        owner: {id: null, type: 'user'},
        bankAccount: null,
    };
    private onChange: (value: any) => void = noop;
    private onTouched: () => void = noop;
    private _control: AbstractControl;
    private _paymentMethod: any = '';

    constructor(
            private _changeDetectorRef: ChangeDetectorRef,
            private _bankAccountsService: BankAccountsService,
            private _organizationService: OrganizationService,
            private _paymentMethodsService: PaymentMethodsService,
            private _stripeService: StripeService,
            private _plaidService: PlaidService,
    ) {
    }

    get control(): AbstractControl {
        return this._control || null;
    }

    @Input('formControl')
    set control(control: AbstractControl) {
        this._control = control;
        this.paymentMethod = this._control.value;
    }

    get paymentMethod(): PaymentMethod {
        return this._paymentMethod;
    }

    @Input('model')
    set paymentMethod(value: PaymentMethod) {
        if (this._paymentMethod !== value) {
            if (this.control === null) {
                const control = new FormControl();
                control.setValue(value);
                this._control = control;
            } else if (this.control && this.control.setValue) {
                this.control.setValue(value);
            }

            this.writeValue(value);

            this.onChange(value);
            this.modelChange.emit(value);
        }
    }

    ngOnInit() {
        this._bankAccountsService.paymentBankAccountChanged.pipe(untilDestroyed(this)).subscribe(
                (paymentBankAccount: PaymentBankAccount) => {
                    this.paymentBankAccountSaved(paymentBankAccount);
                },
        );

        if (this.organizationId) {
            this.microdepositVerificationInputs.owner.id = this.organizationId;
            this.microdepositVerificationInputs.owner.type = 'organization';
        } else {
            const jwt = LegFiJwtService.read();
            this.microdepositVerificationInputs.owner.id = jwt.id;
            this.isUserFlow = true;
        }

        if (!this.organization) {
            this._organizationService.getOrganizationById(this.organizationId
                    ? this.organizationId
                    : null, ['rates']).subscribe(
                    (organization: Organization) => {
                        this.organization = organization;
                        this.getPaymentMethods();
                    },
                    () => {
                        alert('Unable to load organization settings.');
                    },
            );
        } else {
            this.getPaymentMethods();
        }
    }

    getPaymentMethods(): void {
        this.isLoadingPaymentMethods = true;
        if (this.microdepositVerificationInputs.owner.type === 'user') {
            this._paymentMethodsService.getUserPaymentMethods(this.microdepositVerificationInputs.owner.id || null).subscribe(
                    (data: any) => {
                        if (data.hasOwnProperty('creditCards')) {
                            this.creditCards = data['creditCards'];
                        }
                        if (data.hasOwnProperty('bankAccounts')) {
                            this.paymentBankAccounts = data['bankAccounts'];
                        }
                        this.buildPaymentMethods();
                        this.isLoadingPaymentMethods = false;
                    },
            );
        } else {
            this._paymentMethodsService.getOrgPaymentMethods(this.microdepositVerificationInputs.owner.id || null).subscribe(
                    (data: any) => {
                        if (data.hasOwnProperty('creditCards')) {
                            this.creditCards = data['creditCards'];
                        }
                        if (data.hasOwnProperty('bankAccounts')) {
                            this.paymentBankAccounts = data['bankAccounts'];
                        }
                        this.buildPaymentMethods();
                        this.isLoadingPaymentMethods = false;
                    },
            );
        }
    }

    buildPaymentMethods() {
        const paymentMethods: PaymentMethod[] = [];
        if (!this.disableBanks && this.paymentBankAccounts && this.paymentBankAccounts.length) {
            this.paymentBankAccounts.forEach((account) => {
                paymentMethods.push({
                    type: 'PaymentBankAccount',
                    value: account,
                });
            });
        }

        if (this.creditCards && this.creditCards.length) {
            this.creditCards.forEach((card) => {
                paymentMethods.push({
                    type: 'CreditCard',
                    value: card,
                });
            });
        }

        this.defaultPaymentMethods.forEach((pm) => {
            if (pm.value === 'new-bank-account') {
                if (!this.disableBanks) {
                    paymentMethods.push(pm);
                }
            } else {
                paymentMethods.push(pm);
            }
        });
        this.paymentMethods = paymentMethods;

        if (this.paymentMethod && this.paymentMethod.value) {
            let selected: PaymentMethod = null;
            this.paymentMethods.forEach((paymentMethod) => {
                if (paymentMethod.type === this.paymentMethod.type && paymentMethod.value.id === this.paymentMethod.value.id) {
                    selected = paymentMethod;
                }
            });

            if (selected) {
                this.paymentMethod = selected;
            }
        }
    }

    changeSelectedPaymentMethod(selectedMethod: any) {
        this.paymentMethod = selectedMethod;
        this.isPaymentMethodNotSelected = false;

        if (this.paymentMethod && this.paymentMethod.value === 'new-bank-account') {
            return this.bankAccountManagerComponent.addPaymentAccount();
        }

        if (this.paymentMethod && this.paymentMethod.type === 'PaymentBankAccount' && this.paymentMethod.value !== 'new-bank-account' && !this.paymentMethod.value.verifiedMicrodeposits) {
            if (this.paymentMethod.value.pendingAutoVerification || this.paymentMethod.value.pendingManualVerification) {
                this._plaidService.getLinkToken(this.isUserFlow ? 'user' : 'organization', this.paymentMethod.value.id)
                        .subscribe(token => {
                            const plaidObject = Plaid.create({
                                token: token,
                                onSuccess: () => {
                                    this.bankAccountManagerComponent.refreshLinkedAccount(this.paymentMethod.value);
                                },
                                onError: () => {
                                    this.bankAccountManagerComponent.refreshLinkedAccount(this.paymentMethod.value);
                                },
                            });
                            plaidObject.open();
                        });
            } else {
                this.bankAccountManagerComponent.beginManualMicrodepositVerification(this.paymentMethod.value)
                        .then((verifiedAccount: PaymentBankAccount) => {
                            this.resetAccountManager(verifiedAccount);
                        }).catch(() => {
                    this.changeSelectedPaymentMethod(null);
                });
            }
        }

        if (this.organization.rates && this.paymentMethod) {
            const isBankAccountPayment = this.paymentMethod.value === 'new-bank-account' || this.paymentMethod.value.routingNumber;
            const isCCPayment = this.paymentMethod.value === 'new-credit-card' || this.paymentMethod.value.cardBrand;
            const hasPushedCCFees = this.organization.rates.merchantRate > 0 && !this.organization.profile.absorbsCcFees;
            const hasPushedACHFees = this.organization.rates.checkRateFlat > 0 && !this.organization.profile.absorbsAchFees;
            this.showFeeNotification = (isCCPayment && hasPushedCCFees) || (isBankAccountPayment && hasPushedACHFees);
        } else {
            this.showFeeNotification = false;
        }
        this._changeDetectorRef.detectChanges();
    }

    paymentBankAccountSaved(account: PaymentBankAccount) {
        if (!account) {
            return;
        }

        let index = this.findBankAccountIndexById(account.id);
        if (index > -1) {
            this.paymentBankAccounts[index] = account;
        } else {
            this.paymentBankAccounts.push(account);
        }

        this.buildPaymentMethods();

        index = this.findPaymentMethodIndexById(account.id, 'PaymentBankAccount');
        if (index > -1) {
            this.changeSelectedPaymentMethod(this.paymentMethods[index]);
        }

        if (!account.verifiedMicrodeposits) {
            this.changeSelectedPaymentMethod(null);
        }
    }

    bankAccountModalClosed() {
        if (this.paymentMethod && this.paymentMethod.value === 'new-bank-account') {
            this.paymentMethod = null;
        } else if (this.paymentMethod && this.paymentMethod.value && !this.paymentMethod.value.verifiedMicrodeposits) {
            this.changeSelectedPaymentMethod(null);
        }
    }

    creditCardAdded(stripeResponse) {
        let request: Observable<Object>;
        if (this.microdepositVerificationInputs.owner.type === 'user') {
            request = this._stripeService.addUserCreditCard(
                    this.microdepositVerificationInputs.owner.id, stripeResponse.id,
            );
        } else {
            request = this._stripeService.addOrgCreditCard(
                    this.microdepositVerificationInputs.owner.id, stripeResponse.id,
            );
        }

        request.subscribe(
                (data: CreditCard) => {
                    this.creditCards.push(data);
                    this.buildPaymentMethods();

                    const index = this.findPaymentMethodIndexById(data.id, 'CreditCard');
                    if (index > -1) {
                        this.changeSelectedPaymentMethod(this.paymentMethods[index]);
                    }
                },
                () => {
                    alert('Unable to save credit card for ' + this.microdepositVerificationInputs.owner.type + '.');
                },
        );
    }

    resetAccountManager($event: PaymentBankAccount) {
        const index = this.findBankAccountIndexById($event.id);
        if (index !== -1) {
            this.paymentBankAccounts.splice(index, 1);
        }
        this.paymentBankAccounts.push((<PaymentBankAccount>$event));

        const newPaymentMethod = ((<PaymentMethod>{
            type: 'PaymentBankAccount',
            value: $event,
        }));
        this.paymentMethod = newPaymentMethod;
        this.paymentMethods.push(newPaymentMethod);

        if (!this.paymentMethod.value.verifiedMicrodeposits) {
            this.bankAccountManagerComponent.beginManualMicrodepositVerification($event)
                    .then((verifiedAccount: PaymentBankAccount) => {
                        this.resetAccountManager(verifiedAccount);
                    }).catch(() => {
                this.changeSelectedPaymentMethod(null);
            });
        }
    }

    // START ControlValueAccessor implementation
    writeValue(value: any): void {
        this._paymentMethod = value;
    }

    registerOnChange(fn: (_: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    protected findBankAccountIndexById(id: number) {
        for (let i: number = 0; i < this.paymentBankAccounts.length; i++) {
            if (this.paymentBankAccounts[i].id === id) {
                return i;
            }
        }

        return -1;
    }

    protected findPaymentMethodIndexById(id: number, type: string) {
        for (let i: number = 0; i < this.paymentMethods.length; i++) {
            if (this.paymentMethods[i].value.id && this.paymentMethods[i].value.id === id && this.paymentMethods[i].type.indexOf(type) !== -1) {
                return i;
            }
        }

        return -1;
    }

    // END ControlValueAccessor implementation
}
